import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, formatBrandMoneyCompact, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {ArrowRightLeft, Search, Plus, Edit, Trash2, Package, MapPin, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Users, CheckCircle, Clock, AlertCircle, Check, Warehouse, TrendingUp, AlertTriangle, Building2, FileText, Truck, Tag} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import '../../shared/components/custom/CustomComponents.css';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const TransfertsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.transfertsPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  useArticlesConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-transfers-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-transfers-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [transfers, setTransfers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [transferFormTab, setTransferFormTab] = useState('transferInfo');

  const transferFormTabs = useMemo(() => ([
    { id: 'transferInfo', label: p('section_transfer_info'), icon: ArrowRightLeft, iconColor: 'text-blue-400' },
    { id: 'logistics', label: p('section_logistics'), icon: Truck, iconColor: 'text-purple-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    transfer_number: '', transfer_date: new Date().toISOString().split('T')[0],
    from_warehouse: '', to_warehouse: '', product_name: '', product_code: '',
    quantity: '', unit_cost: '', total_value: '', reason: '',
    status: 'en_attente', requested_by: '', approved_by: '', notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_transfers');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Separate effect for status dropdown click-outside handling

  const loadTransfers = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/transfers');
      setTransfers(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading transfers:', error);
      setTransfers([]);
    }
  };

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const loadDeliveryNotes = async () => {
    try {
      // Fetch "Bon de transfert" documents from Facturation
      const response = await localApiAdapter.get('/facturation/archives');
      const documents = response?.data || response || [];
      
      // Filter for "Bon de transfert" documents only
      const transferDocuments = documents
        .filter(doc => doc.type === 'bon-transfert' || doc.documentType === 'bon-transfert')
        .map(doc => ({
          id: doc.id,
          number: doc.documentNumber || doc.number,
          date: doc.date || doc.issueDate,
          product_name: doc.items?.[0]?.description || '',
          product_code: doc.items?.[0]?.productCode || '',
          quantity: doc.items?.[0]?.quantity || 0,
          from_warehouse: doc.fromWarehouse || '',
          to_warehouse: doc.toWarehouse || '',
          carrier: doc.carrier || '',
          transport_cost: doc.transportCost || 0,
          responsable: doc.responsable || doc.createdBy || '',
          transfer_reason: doc.notes || doc.reason || ''
        }));
      
      setDeliveryNotes(transferDocuments);
    } catch (error) {
      console.error('Error loading transfer documents:', error);
      setDeliveryNotes([]);
    }
  };

  // Handler for when delivery note is selected - auto-fills form
  const handleDeliveryNoteSelect = (deliveryNoteNumber) => {
    const selectedNote = deliveryNotes.find(dn => dn.number === deliveryNoteNumber);
    if (selectedNote) {
      // Auto-fill all fields from the delivery note
      setFormData({
        ...formData,
        delivery_note_number: selectedNote.number,
        transfer_date: selectedNote.date,
        product_name: selectedNote.product_name || '',
        product_code: selectedNote.product_code || '',
        quantity: selectedNote.quantity?.toString() || '',
        from_warehouse: selectedNote.from_warehouse || '',
        to_warehouse: selectedNote.to_warehouse || '',
        carrier: selectedNote.carrier || '',
        transport_cost: selectedNote.transport_cost?.toString() || '',
        responsable: selectedNote.responsable || '',
        transfer_reason: selectedNote.transfer_reason || ''
      });
    } else {
      // User typed a custom delivery note number - just update that field
      setFormData({ ...formData, delivery_note_number: deliveryNoteNumber });
    }
  };

  useEffect(() => { 
    loadTransfers(); 
    loadWarehouses();
    loadProducts();
    loadDeliveryNotes();
  }, []);

  // Get unique warehouses for distribution
  const uniqueWarehouses = [...new Set([...transfers.map(t => t.from_warehouse), ...transfers.map(t => t.to_warehouse)].filter(Boolean))];
  const warehouseDistribution = uniqueWarehouses.slice(0, 6).map((warehouse, idx) => {
    const colors = [
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-green-500 to-green-600',
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-orange-500 to-orange-600',
      'bg-gradient-to-r from-red-500 to-red-600',
      'bg-gradient-to-r from-yellow-500 to-yellow-600'
    ];
    return {
      name: warehouse,
      count: transfers.filter(t => t.from_warehouse === warehouse || t.to_warehouse === warehouse).length,
      color: colors[idx % colors.length]
    };
  });

  const stats = {
    total: transfers.length,
    en_attente: transfers.filter(t => t.status === 'en_attente').length,
    en_cours: transfers.filter(t => t.status === 'en_cours').length,
    termine: transfers.filter(t => t.status === 'termine').length,
    valeur_totale: transfers.reduce((sum, t) => sum + (parseFloat(t.total_value) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_transfers', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Layers },
    { id: 'par_entrepot', name: p('tab_by_warehouse'), icon: Warehouse }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'en_attente', name: p('status_pending'), icon: Clock },
    { id: 'en_cours', name: p('status_in_progress'), icon: TrendingUp },
    { id: 'termine', name: p('status_completed'), icon: CheckCircle }
  ];

  const statusDistributionItems = [
    { filterKey: 'en_attente', name: p('status_pending'), count: stats.en_attente, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { filterKey: 'en_cours', name: p('status_in_progress'), count: stats.en_cours, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { filterKey: 'termine', name: p('status_completed'), count: stats.termine, color: 'bg-gradient-to-r from-green-500 to-green-600' },
    { filterKey: 'annule', name: p('status_cancelled'), count: transfers.filter(t => t.status === 'annule').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
  ];

  const transfersDistribution = {
    all_transfers: {
      title: p('overview_title'),
      data: statusDistributionItems
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: statusDistributionItems
    },
    par_entrepot: {
      title: p('distribution_by_warehouse'),
      data: warehouseDistribution.length > 0 ? warehouseDistribution.map(w => ({ ...w, filterKey: w.name })) : [
        { filterKey: null, name: p('no_warehouse'), count: 0, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    }
  };

  const distributionInfo = transfersDistribution[mainViewMode] || transfersDistribution.all_transfers;

  const baseFilteredTransfers = transfers.filter(t => {
    if (!inventaireRecordInDateRange(dateRange, t.transfer_date, t.created_at)) return false;

    if (searchTerm && !t.transfer_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.from_warehouse?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.to_warehouse?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_transfers') {
      if (quickFilter === 'en_attente' && t.status !== 'en_attente') return false;
      if (quickFilter === 'en_cours' && t.status !== 'en_cours') return false;
      if (quickFilter === 'termine' && t.status !== 'termine') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_transfers':
        case 'par_statut':
          if (t.status !== activeDistributionFilter) return false;
          break;
        case 'par_entrepot':
          if (activeDistributionFilter && t.from_warehouse !== activeDistributionFilter && t.to_warehouse !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('transfer_number'),
    { key: 'transfer_date', getValue: (r) => new Date(r.transfer_date).toLocaleDateString(dateLocale) },
    { key: 'warehouses', getValue: (r) => `${r.from_warehouse || ''} ${r.to_warehouse || ''}`.trim() },
    textColumnRule('product_name'),
    textColumnRule('quantity', (r) => String(r.quantity ?? '')),
    textColumnRule('total_value', (r) => String(r.total_value ?? '')),
    exactColumnRule('status', (r) => r.status),
  ], [dateLocale]);

  const filteredTransfers = useMemo(
    () => applyColumnFilters(baseFilteredTransfers, colFilters, columnFilterRules),
    [baseFilteredTransfers, colFilters, columnFilterRules],
  );
  const visibleTransfers = useMemo(
    () => sliceInventaireListItems(filteredTransfers, itemsPerPage),
    [filteredTransfers, itemsPerPage],
  );
  const generateTransferNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'transfer', 'TRF', transfers, (t) => t.transfer_number),
    [settings, transfers],
  );

  const openNewTransferModal = () => {
    if (!inventoryConfig.enableTransfers) {
      window.alert(p('notice_transfers_disabled', { defaultValue: 'التحويلات معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setTransferFormTab('transferInfo');
    setFormData({
      transfer_number: inventoryConfig.autoGenerateTransferReference !== false ? generateTransferNumber() : '',
      transfer_date: new Date().toISOString().split('T')[0],
      from_warehouse: '',
      to_warehouse: '',
      product_name: '',
      product_code: '',
      quantity: '',
      unit_cost: '',
      total_value: '',
      reason: '',
      status: 'en_attente',
      requested_by: '',
      approved_by: '',
      notes: '',
      delivery_note_number: '',
      transfer_reason: '',
      transport_cost: '',
      responsable: '',
      expected_arrival_date: '',
      carrier: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const transferNumber = (formData.transfer_number || '').trim() || generateTransferNumber();
      const submitData = { 
        ...formData,
        transfer_number: transferNumber,
        quantity: parseFloat(formData.quantity) || 0,
        unit_cost: parseFloat(formData.unit_cost) || 0,
        total_value: parseFloat(formData.total_value) || 0
      };
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/transfers/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'النقل' }));
      } else {
        await localApiAdapter.post('/inventory/transfers', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'النقل' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadTransfers();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      transfer_number: record.transfer_number || '', transfer_date: record.transfer_date || new Date().toISOString().split('T')[0],
      from_warehouse: record.from_warehouse || '', to_warehouse: record.to_warehouse || '',
      product_name: record.product_name || '', product_code: record.product_code || '',
      quantity: record.quantity?.toString() || '', unit_cost: record.unit_cost?.toString() || '',
      total_value: record.total_value?.toString() || '',
      status: record.status || 'en_attente',
      transfer_reason: record.transfer_reason || '',
      transport_cost: record.transport_cost?.toString() || '', 
      responsable: record.responsable || '', expected_arrival_date: record.expected_arrival_date || '', 
      carrier: record.carrier || '', delivery_note_number: record.delivery_note_number || ''
    });
    setTransferFormTab('transferInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/transfers/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'النقل' }));
        loadTransfers();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredTransfersIds = useMemo(() => filteredTransfers.map((r) => r.id), [filteredTransfers]);
  const allRowsSelected = filteredTransfersIds.length > 0 && filteredTransfersIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredTransfersIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/transfers/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'النقل' }));
        setSelectedRecords([]);
        loadTransfers();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      transfer_number: '', transfer_date: new Date().toISOString().split('T')[0],
      from_warehouse: '', to_warehouse: '', product_name: '', product_code: '',
      quantity: '', unit_cost: '', total_value: '', reason: '',
      status: 'en_attente', requested_by: '', approved_by: '', notes: ''
    });
    setEditingRecord(null);
    setTransferFormTab('transferInfo');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'transfer_number', label: p('col_transfer_number') },
      { key: 'transfer_date', label: p('col_date') },
      { key: 'from_warehouse', label: p('export_from') },
      { key: 'to_warehouse', label: p('export_to') },
      { key: 'product_name', label: p('col_product') },
      { key: 'quantity', label: p('col_quantity') },
      { key: 'status', label: p('col_status') }
    ];
    const fileName = `transferts_export_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredTransfers, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('col_transfer_number')}</th><th>${p('col_date')}</th><th>${p('export_from')}</th><th>${p('export_to')}</th><th>${p('col_product')}</th><th>${p('col_quantity')}</th><th>${p('col_status')}</th></tr></thead><tbody>${filteredTransfers.map(item => `<tr><td>${item.transfer_number || p('na')}</td><td>${item.transfer_date || p('na')}</td><td>${item.from_warehouse || p('na')}</td><td>${item.to_warehouse || p('na')}</td><td>${item.product_name || p('na')}</td><td>${item.quantity || 0}</td><td>${item.status || p('na')}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('col_transfer_number')}</th><th>${p('col_date')}</th><th>${p('export_from')}</th><th>${p('export_to')}</th><th>${p('col_product')}</th><th>${p('export_code')}</th><th>${p('col_quantity')}</th><th>${p('export_cost')}</th><th>${p('col_value')}</th><th>${p('col_status')}</th></tr></thead><tbody>${filteredTransfers.map(item => `<tr><td>${item.transfer_number || ''}</td><td>${item.transfer_date || ''}</td><td>${item.from_warehouse || ''}</td><td>${item.to_warehouse || ''}</td><td>${item.product_name || ''}</td><td>${item.product_code || ''}</td><td>${item.quantity || ''}</td><td>${item.unit_cost || ''}</td><td>${item.total_value || ''}</td><td>${item.status || ''}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportExcel('transferts', p('report_excel_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'transfer_number', label: p('col_transfer_number') },
      { key: 'from_warehouse', label: p('export_from') },
      { key: 'to_warehouse', label: p('export_to') },
      { key: 'product_name', label: p('col_product') },
      { key: 'quantity', label: p('col_quantity') }
    ];

    const schema = {
      requiredFields: ['transfer_number', 'from_warehouse', 'to_warehouse', 'product_name', 'quantity'],
      validators: {
        status: (value) => !value || ['en_attente', 'en_cours', 'termine', 'annule'].includes(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/inventory/transfers', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadTransfers();
          const message = `${p('import_success_header')}\n\n${p('import_success_total', { total: result.totalCount })}\n${p('import_success_imported', { imported: successCount })}\n` + (result.invalidCount > 0 ? `${p('import_errors', { count: result.invalidCount })}\n\n${result.errors.slice(0, 5).join('\n')}` : '');
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const statusOptions = [
    { value: 'en_attente', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'en_cours', label: p('status_in_progress'), icon: TrendingUp, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'termine', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'annule', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const transferReasonOptions = [
    { value: 'Réapprovisionnement', label: p('reason_restock') },
    { value: 'Demande client', label: p('reason_client_request') },
    { value: 'Équilibrage stock', label: p('reason_stock_balance') },
    { value: 'Retour', label: p('reason_return') },
    { value: 'Maintenance', label: p('reason_maintenance') },
    { value: 'Autre', label: p('reason_other') }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/transfers/${recordId}`, { status: newStatus });
      await loadTransfers();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };


  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'transfer_number', labelKey: 'col_transfer_number' },
    { key: 'transfer_date', labelKey: 'col_date' },
    { key: 'warehouses', labelKey: 'col_warehouses' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'quantity', labelKey: 'col_quantity' },
    { key: 'total_value', labelKey: 'col_value' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Package },
    { key: 'stat_pending', label: p('stat_pending'), value: stats.pending, icon: Clock },
    { key: 'stat_in_transit', label: p('stat_in_transit'), value: stats.in_transit, icon: TrendingUp },
    { key: 'stat_completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle },
  ], 4), [stats, p]);


  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableTransfers ? p('notice_transfers_disabled_banner', { defaultValue: 'التحويلات معطّلة — فعّلها من إعدادات المخزون ← التحويلات.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Package}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewTransferModal}
      createLabel={p('new_transfer')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  {isColVisible('transfer_number') && (

                    <TableColumnHeaderFilter columnKey="transfer_number" label={p('col_transfer_number')} value={colFilters.transfer_number ?? ''} onChange={(v) => setColFilter('transfer_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('transfer_date') && (

                    <TableColumnHeaderFilter columnKey="transfer_date" label={p('col_date')} value={colFilters.transfer_date ?? ''} onChange={(v) => setColFilter('transfer_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('warehouses') && (

                    <TableColumnHeaderFilter columnKey="warehouses" label={p('col_warehouses')} value={colFilters.warehouses ?? ''} onChange={(v) => setColFilter('warehouses', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('quantity') && (

                    <TableColumnHeaderFilter columnKey="quantity" label={p('col_quantity')} value={colFilters.quantity ?? ''} onChange={(v) => setColFilter('quantity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('total_value') && (

                    <TableColumnHeaderFilter columnKey="total_value" label={p('col_value')} value={colFilters.total_value ?? ''} onChange={(v) => setColFilter('total_value', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleTransfers.length > 0 ? (
                  visibleTransfers.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('transfer_number') && (

                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.transfer_number}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('requested_by')}: {record.requested_by || p('na')}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('transfer_date') && (

                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{new Date(record.transfer_date).toLocaleDateString(dateLocale)}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('warehouses') && (

                        <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Warehouse size={14} className="text-red-400" />
                              {record.from_warehouse || '-'}
                            </div>
                          </div>
                          <ArrowRightLeft size={16} className="text-white/50" />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Warehouse size={14} className="text-green-400" />
                              {record.to_warehouse || '-'}
                            </div>
                          </div>
                        </div>
                      </td>

                      )}
                      {isColVisible('product_name') && (

                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('code_label')}: {record.product_code || p('na')}</span>
                        </div>
                      </td>

                      )}
                      {isColVisible('quantity') && (

                        <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.quantity || 0}</span>
                      </td>

                      )}
                      {isColVisible('total_value') && (

                        <td className="px-6 py-4">
                        <span className="text-white font-semibold">{formatBrandMoney(record.total_value || 0)}</span>
                      </td>

                      )}
                      {isColVisible('status') && (

                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ArrowRightLeft size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleTransfers.length > 0 ? (
              visibleTransfers.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ArrowRightLeft size={20} className="text-blue-400" />
                          {record.transfer_number}
                        </h3>
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} />
                        <span className="text-white/50 text-sm">{new Date(record.transfer_date).toLocaleDateString(dateLocale)}</span>
                      </div>
                      <div className={INVENTAIRE_FORM_GRID}>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_warehouses')}</h5>
                          <div className="space-y-2 text-sm text-white/70">
                            <div className="flex items-center gap-1.5"><Warehouse size={14} className="text-red-400" /><span className="font-medium">{p('label_from')}</span> {record.from_warehouse || '-'}</div>
                            <div className="flex items-center gap-1.5"><Warehouse size={14} className="text-green-400" /><span className="font-medium">{p('label_to')}</span> {record.to_warehouse || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.product_name || '-'}</div>
                            <div><span className="font-medium">{p('label_code')}</span> {record.product_code || p('na')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_qty_value')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_qty')}</span> {record.quantity || 0}</div>
                            <div><span className="font-medium">{p('label_cost')}</span> {formatBrandMoney(record.unit_cost || 0)}</div>
                            <div><span className="font-medium">{p('label_total')}</span> {formatBrandMoney(record.total_value || 0)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_request_approval')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_requested')}</span> {record.requested_by || p('na')}</div>
                            <div><span className="font-medium">{p('label_approved')}</span> {record.approved_by || p('approval_pending')}</div>
                            {record.reason && <div className="mt-2 p-2 bg-white/5 rounded"><span className="font-medium text-white/80 text-xs">{p('label_reason')}</span><p className="text-white/60 text-xs mt-1">{record.reason}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><ArrowRightLeft size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      
      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1000px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_transfer') : p('new_transfer')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="transfer-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={transferFormTabs}
              activeTab={transferFormTab}
              onTabChange={setTransferFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_transfer') : p('new_transfer')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="transfer-form" onSubmit={handleSubmit}>
              {transferFormTab === 'transferInfo' && (
              <div className={INVENTAIRE_FORM_GRID}>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transfer_number', { defaultValue: 'رمز التحويل' })}</label>
                            <input
                              type="text"
                              required
                              value={formData.transfer_number || ''}
                              onChange={(e) => setFormData({ ...formData, transfer_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                              placeholder={p('placeholder_transfer_number', { defaultValue: 'TRF-001' })}
                              dir="ltr"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2">
                              <FileText size={16} className="text-yellow-400" />
                              {p('label_delivery_note')}
                            </label>
                            <CustomSelect
                              value={formData.delivery_note_number}
                              onChange={handleDeliveryNoteSelect}
                              options={deliveryNotes.map(dn => ({
                                value: dn.number,
                                label: `${dn.number} - ${dn.date}${dn.product_name ? ` (${dn.product_name})` : ''}`
                              }))}
                              placeholder={p('placeholder_delivery_note')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                            <CustomDatePicker
                              value={formData.transfer_date}
                              onChange={(date) => setFormData({ ...formData, transfer_date: date })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'en_attente', label: p('status_pending') },
                                { value: 'en_cours', label: p('status_in_progress') },
                                { value: 'complete', label: p('status_completed') },
                                { value: 'annule', label: p('status_cancelled') }
                              ]}
                              placeholder={p('placeholder_status')}
                              required
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_source_warehouse')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.from_warehouse}
                              onChange={(value) => setFormData({ ...formData, from_warehouse: value })}
                              options={warehouses.map(wh => ({
                                value: wh.name,
                                label: `${wh.name}${wh.code ? ` (${wh.code})` : ''}`
                              }))}
                              placeholder={p('placeholder_source')}
                              searchable={true}
                              required
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_dest_warehouse')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.to_warehouse}
                              onChange={(value) => setFormData({ ...formData, to_warehouse: value })}
                              options={warehouses.map(wh => ({
                                value: wh.name,
                                label: `${wh.name}${wh.code ? ` (${wh.code})` : ''}`
                              }))}
                              placeholder={p('placeholder_dest')}
                              searchable={true}
                              required
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_product')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.product_name}
                              onChange={(value) => {
                                const selectedProduct = products.find(p => p.name === value);
                                if (selectedProduct) {
                                  setFormData({ 
                                    ...formData, 
                                    product_name: selectedProduct.name,
                                    product_code: selectedProduct.sku || '',
                                    unit_cost: selectedProduct.price || ''
                                  });
                                } else {
                                  setFormData({ ...formData, product_name: value });
                                }
                              }}
                              options={products.map(p => ({
                                value: p.name,
                                label: `${p.name}${p.sku ? ` (${p.sku})` : ''}`
                              }))}
                              placeholder={p('placeholder_product')}
                              searchable={true}
                              required
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_quantity')} <span className="qawaem-required-mark">*</span></label>
                            <input 
                              type="number" 
                              required 
                              value={formData.quantity} 
                              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder="100" 
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transfer_reason')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.transfer_reason}
                              onChange={(value) => setFormData({ ...formData, transfer_reason: value })}
                              options={transferReasonOptions}
                              placeholder={p('placeholder_reason')}
                              searchable={true}
                              creatable={true}
                              required
                            />
                          </div></div>
              )}

              {transferFormTab === 'logistics' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transport_cost')}</label>
                            <input 
                              type="number" 
                              value={formData.transport_cost} 
                              onChange={(e) => setFormData({ ...formData, transport_cost: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder="250.00" 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_responsible')}</label>
                            <CustomSelect
                              value={formData.responsable}
                              onChange={(value) => setFormData({ ...formData, responsable: value })}
                              options={[]}
                              placeholder={p('placeholder_responsible')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_expected_arrival')}</label>
                            <input 
                              type="date" 
                              value={formData.expected_arrival_date} 
                              onChange={(e) => setFormData({ ...formData, expected_arrival_date: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_carrier')}</label>
                            <CustomSelect
                              value={formData.carrier}
                              onChange={(value) => setFormData({ ...formData, carrier: value })}
                              options={[]}
                              placeholder={p('placeholder_carrier')}
                              searchable={true}
                              creatable={true}
                            />
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}

      {showViewModal && viewingRecord && (
        <InventaireViewModalPortal show={showViewModal} record={viewingRecord} onClose={() => setShowViewModal(false)} maxWidth="720px">
          <div className="flex justify-between items-start mb-6 p-6 pb-0">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{p('view_title')}</h2>
                <p className="text-white/60">{p('transfer_number_prefix', { number: viewingRecord.transfer_number })}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
            </div>
            <div className="space-y-4 text-white">
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="font-semibold mb-3 text-blue-300">{p('section_info')}</h3>
                <div className={`${INVENTAIRE_FORM_GRID} text-sm`}>
                  <div><span className="text-white/60">{p('label_date')}:</span> <span className="font-medium">{viewingRecord.transfer_date}</span></div>
                  <div><span className="text-white/60">{p('label_status')}:</span> <span className="font-medium">{viewingRecord.status}</span></div>
                  <div><span className="text-white/60">{p('from')}:</span> <span className="font-medium">{viewingRecord.from_warehouse}</span></div>
                  <div><span className="text-white/60">{p('to')}:</span> <span className="font-medium">{viewingRecord.to_warehouse}</span></div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="font-semibold mb-3 text-green-300">{p('section_product')}</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-white/60">{p('label_name')}</span> <span className="font-medium">{viewingRecord.product_name}</span></div>
                  <div><span className="text-white/60">{p('col_quantity')}:</span> <span className="font-medium">{viewingRecord.quantity}</span></div>
                  <div><span className="text-white/60">{p('col_value')}:</span> <span className="font-medium">{formatBrandMoney(viewingRecord.total_value || 0)}</span></div>
                </div>
              </div>
              {viewingRecord.transfer_reason && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 text-purple-400">{p('section_transfer_reason')}</h3>
                  <p className="text-sm text-white/90">{viewingRecord.transfer_reason}</p>
                </div>
              )}
              {/* Logistics Information */}
              {(viewingRecord.transport_cost || viewingRecord.responsable || viewingRecord.expected_arrival_date || viewingRecord.carrier || viewingRecord.delivery_note_number) && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="font-semibold mb-3 text-blue-400 flex items-center gap-2">
                    <Truck size={18} />
                    {p('section_logistics')}
                  </h3>
                  <div className={`${INVENTAIRE_FORM_GRID} text-sm`}>
                    {viewingRecord.transport_cost && (
                      <div>
                        <span className="text-white/60">{p('label_transport_cost_view')}</span>
                        <span className="ml-2 text-white/90">{formatBrandMoney(viewingRecord.transport_cost || 0)}</span>
                      </div>
                    )}
                    {viewingRecord.responsable && (
                      <div>
                        <span className="text-white/60">{p('label_responsible_view')}</span>
                        <span className="ml-2 text-white/90">{viewingRecord.responsable}</span>
                      </div>
                    )}
                    {viewingRecord.expected_arrival_date && (
                      <div>
                        <span className="text-white/60">{p('label_expected_arrival_view')}</span>
                        <span className="ml-2 text-white/90">{viewingRecord.expected_arrival_date}</span>
                      </div>
                    )}
                    {viewingRecord.carrier && (
                      <div>
                        <span className="text-white/60">{p('label_carrier_view')}</span>
                        <span className="ml-2 text-white/90">{viewingRecord.carrier}</span>
                      </div>
                    )}
                    {viewingRecord.delivery_note_number && (
                      <div>
                        <span className="text-white/60">{p('label_delivery_note_number')}</span>
                        <span className="ml-2 text-white/90">{viewingRecord.delivery_note_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
        </InventaireViewModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default TransfertsPage;


